import { connectFunctionsEmulator, httpsCallable } from "firebase/functions";
import { functions } from "../config/firebaseConfig.ts";
import {
  ChildVenue,
  CombinedFilmObject,
  DataFromGetScreenings,
  FilmMetaDataMap,
  FilmsInDateRange,
  ParentVenue,
  PopularFilmOverview,
  ScreeningsInDateRange,
  VenueNameAndSlug,
} from "../types/dataTypes.ts";
import { fetchJsonFromApiOrThrow } from "./fetchHelpers.ts";

if (process.env.NODE_ENV === "development") {
  connectFunctionsEmulator(functions, "localhost", 5001);
}

function mergeScreeningsAndFilmData(
  screenings: ScreeningsInDateRange,
  filmMetaDataMap: FilmMetaDataMap,
) {
  return {
    ...screenings,
    film_data: screenings.film_data.map((film) => ({
      ...film,
      ...filmMetaDataMap[film.title],
    })),
  } as FilmsInDateRange;
}

const API_URL =
  process.env.NODE_ENV === "development"
    ? "http://localhost:5001/cinema-guide-ireland/europe-west2/api"
    : "https://europe-west2-cinema-guide-ireland.cloudfunctions.net/api";

export const fetchScreenings = async (
  venues: string[],
  page: string,
  initial_view: boolean,
): Promise<{
  all_screenings_on_all_dates: FilmsInDateRange;
  screenings_by_date: FilmsInDateRange[];
}> => {
  const {
    all_screenings_on_all_dates,
    screenings_by_date,
    film_meta_data_map,
  } = await fetchJsonFromApiOrThrow<DataFromGetScreenings>(
    `${API_URL}/getScreenings`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ venues, page, initial_view }),
    },
  );

  return {
    all_screenings_on_all_dates: mergeScreeningsAndFilmData(
      all_screenings_on_all_dates,
      film_meta_data_map,
    ),
    screenings_by_date: screenings_by_date.map(
      (screeningsData: ScreeningsInDateRange) =>
        mergeScreeningsAndFilmData(screeningsData, film_meta_data_map),
    ),
  };
};

export const fetchVenuesByLocation = async ({
  latitude,
  longitude,
}: {
  latitude: number;
  longitude: number;
}): Promise<ChildVenue[]> => {
  const response = httpsCallable(functions, "getVenuesWithinRadius");
  return response({ userLatitude: latitude, userLongitude: longitude }).then(
    (response) => {
      return response.data as ChildVenue[];
    },
  );
};

export const recordExternalScreeningClick = async (
  filmTitle: string,
  venueName: string,
  time: string,
) => {
  if (process.env.NODE_ENV !== "development") {
    const response = httpsCallable(functions, "recordExternalScreeningClick");
    return response({
      film_title: filmTitle,
      venue_name: venueName,
      time_of_screening: time,
    });
  }
};

export const fetchPopularFilmsOverview = async () => {
  return fetchJsonFromApiOrThrow<PopularFilmOverview>(
    `${API_URL}/getPopularFilmsOverview`,
    {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    },
  );
};

export const fetchScreeningsForIndividualPopularFilm = async (
  city: string,
  film_title: string,
): Promise<CombinedFilmObject | null> => {
  try {
    return await fetchJsonFromApiOrThrow<CombinedFilmObject>(
      `${API_URL}/getScreeningsForIndividualPopularFilm`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ city, film_title }),
      },
    );
  } catch (e: any) {
    if (e?.code === "not-found" || e?.status === 404) return null;
    throw e;
  }
};

export const fetchAllVenues = async () => {
  const response = httpsCallable(functions, "getAllVenues");
  return response().then((response) => {
    return response.data as VenueNameAndSlug[];
  });
};

export const getIndividualVenue = async (
  venue_slug: string,
): Promise<ParentVenue | null> => {
  try {
    const fn = httpsCallable(functions, "getIndividualVenue");
    const res = await fn({ venue_slug });
    return res.data as ParentVenue;
  } catch (e) {
    console.error(e);
    return null;
  }
};
